import {
  comparisonTolerance,
  MAX_RATING_DROP,
  MIN_BIGGEST_SAVING,
  MIN_REVIEWS_FOR_BEST_RATED,
  MIN_UPGRADE_RATING_GAIN,
  MIN_UPGRADE_SAVING
} from '../constants'

import type {SimilarHotel, SimilarHotelBadge} from '../types'
import type {Hotel} from 'modules/sapiSearch/slice'

export const awardSimilarHotelBadge = (
  badges: Record<string, SimilarHotelBadge>,
  badge: SimilarHotelBadge,
  eligible: SimilarHotel[],
  compare: (a: SimilarHotel, b: SimilarHotel) => number
) => {
  const winner = eligible
    .filter(item => !badges[item.hotel.objectID])
    .sort(compare)[0]
  if (winner) badges[winner.hotel.objectID] = badge
}

/** Award each badge once, in priority order, without giving a hotel two badges. */
export const getSimilarHotelBadges = (
  candidates: SimilarHotel[],
  anchor: Hotel
) => {
  const badges: Record<string, SimilarHotelBadge> = {}

  const byRating = (a: SimilarHotel, b: SimilarHotel) =>
    b.hotel.guestRating.overall - a.hotel.guestRating.overall
  const bySaving = (a: SimilarHotel, b: SimilarHotel) =>
    a.priceDifference - b.priceDifference

  awardSimilarHotelBadge(
    badges,
    'upgradeAndSave',
    candidates.filter(
      item =>
        item.ratingDifference >=
          MIN_UPGRADE_RATING_GAIN - comparisonTolerance &&
        anchor?.starRating > 0 &&
        item.hotel.starRating >= anchor.starRating &&
        item.priceDifference <= -MIN_UPGRADE_SAVING + comparisonTolerance
    ),
    (a, b) => byRating(a, b) || bySaving(a, b)
  )

  awardSimilarHotelBadge(
    badges,
    'biggestSaving',
    candidates.filter(
      item =>
        item.priceDifference <= -MIN_BIGGEST_SAVING + comparisonTolerance &&
        item.ratingDifference >= -MAX_RATING_DROP - comparisonTolerance
    ),
    bySaving
  )

  const ratedCandidates = candidates.filter(
    item =>
      item.hotel.guestRating?.overall > 0 &&
      item.hotel.reviewCount >= MIN_REVIEWS_FOR_BEST_RATED
  )
  const highestRating = Math.max(
    ...ratedCandidates.map(item => item.hotel.guestRating.overall)
  )

  // A higher-priority badge must not make a lower-rated hotel look best rated.
  awardSimilarHotelBadge(
    badges,
    'bestRatedNearby',
    ratedCandidates.filter(
      item => item.hotel.guestRating.overall === highestRating
    ),
    byRating
  )

  return badges
}
